// Feeding plan: how many meals, when, how much, and when the bag runs out.

import { mealsPerDay } from './age.ts';
import { addDays, atClock, dayStart, daysBetween, formatTime, startOfDay, today } from './time.ts';
import type { FoodConfig, Household, ISODate, LogEvent, Millis } from './types.ts';

export const DEFAULT_MEAL_TIMES: Record<2 | 3 | 4, string[]> = {
  4: ['07:00', '11:00', '15:00', '18:30'],
  3: ['07:00', '12:30', '18:00'],
  2: ['07:30', '17:30'],
};

export interface PlannedMeal {
  index: number;
  time: string;
  at: Millis;
  grams?: number;
  eaten?: LogEvent<'meal'>;
}

export interface MealPlan {
  perDay: 2 | 3 | 4;
  times: string[];
  gramsPerMeal?: number;
  meals: PlannedMeal[];
  next?: PlannedMeal;
  eatenToday: number;
  gramsToday: number;
}

export function mealTimes(household: Pick<Household, 'mealTimes'> | undefined, perDay: 2 | 3 | 4): string[] {
  const custom = household?.mealTimes?.[perDay];
  return custom && custom.length === perDay ? custom : DEFAULT_MEAL_TIMES[perDay];
}

/**
 * Build today's meal plan and match logged meals to slots in order.
 * A meal logged at any time fills the earliest unfilled slot, so a late
 * breakfast still counts as breakfast.
 */
export function mealPlan(opts: {
  ageWeeks: number;
  food: FoodConfig;
  household?: Pick<Household, 'mealTimes'>;
  events: LogEvent[];
  now: Millis;
}): MealPlan {
  const perDay = mealsPerDay(opts.ageWeeks);
  const times = mealTimes(opts.household, perDay);
  const gramsPerMeal = opts.food.dailyGrams ? Math.round(opts.food.dailyGrams / perDay) : undefined;
  const todayStart = startOfDay(opts.now);
  const todaysMeals = opts.events
    .filter((e): e is LogEvent<'meal'> => e.type === 'meal' && e.at >= todayStart && e.at < dayStart(opts.now, 1))
    .sort((a, b) => a.at - b.at);

  const meals: PlannedMeal[] = times.map((time, index) => ({
    index,
    time,
    at: atClock(opts.now, time),
    grams: gramsPerMeal,
    eaten: todaysMeals[index],
  }));
  const next = meals.find((m) => !m.eaten);
  const gramsToday = todaysMeals.reduce((sum, m) => sum + (m.data.grams ?? gramsPerMeal ?? 0), 0);
  return { perDay, times, gramsPerMeal, meals, next, eatenToday: todaysMeals.length, gramsToday };
}

export function describeMeal(meal: PlannedMeal, perDay: number): string {
  const names: Record<number, string[]> = {
    4: ['Breakfast', 'Lunch', 'Afternoon', 'Dinner'],
    3: ['Breakfast', 'Lunch', 'Dinner'],
    2: ['Breakfast', 'Dinner'],
  };
  return `${names[perDay]?.[meal.index] ?? `Meal ${meal.index + 1}`} · ${formatTime(meal.at)}`;
}

export interface FoodStock {
  daysLeft: number;
  runsOut: ISODate;
  reorderBy: ISODate;
  percentLeft: number;
}

/** Estimate when the current bag runs out, assuming the daily ration is fed. */
export function foodStock(food: FoodConfig, now: Millis = Date.now()): FoodStock | undefined {
  if (!food.bagKg || !food.bagOpened || !food.dailyGrams) return undefined;
  const totalDays = Math.floor((food.bagKg * 1000) / food.dailyGrams);
  const used = Math.max(0, daysBetween(food.bagOpened, today(now)));
  const daysLeft = Math.max(0, totalDays - used);
  const runsOut = addDays(food.bagOpened, totalDays);
  return {
    daysLeft,
    runsOut,
    reorderBy: addDays(runsOut, -7),
    percentLeft: totalDays ? daysLeft / totalDays : 0,
  };
}

export interface TransitionDay {
  day: number;
  date: ISODate;
  newPercent: number;
}

/**
 * Gradual food change from the breeder's food: ~25% new for a few days,
 * then 50%, 75%, 100% over about 10 days. Pause if tummies get upset.
 */
export function transitionPlan(start: ISODate): TransitionDay[] {
  const steps = [25, 25, 25, 50, 50, 50, 75, 75, 75, 100];
  return steps.map((newPercent, i) => ({ day: i + 1, date: addDays(start, i), newPercent }));
}
